// Business Management → Reports (PRD rev. 1.22, FR1–FR8), modelled on Monarch's reports: cash flow,
// spending by category, income by client, and the flow from clients to categories, over QuickBooks's
// synced lines. The API sends resolved lines; ranges regroup, filters and drill-downs happen here.
// Admins only (books.read, SEC20).

import { useMemo, useState } from "react";
import { Badge, Body1, Button, Caption1, Card, MessageBar, MessageBarBody, Select, Subtitle2, Switch, Tab, TabList } from "@fluentui/react-components";
import {
  addMonths,
  cashFlowPeriods,
  compareRange,
  flowLinks,
  incomeByClient,
  linesFor,
  monthOf,
  NOT_ASSIGNED,
  spendingAt,
  spendingByProject,
  tieOut,
  today,
  UNMAPPED,
  type BooksTotals,
  type CodingProject,
  type ReportGrouping,
  type ReportLine,
  type Slice,
  type SpendCategory,
} from "../../../core";
import { errorText, useApi, api } from "../api";
import { DateInput } from "../DateInput";
import { day, money, pct } from "../format";
import { go, href } from "../router";
import { ErrorBar, Kpi, Loading, Page, useMe } from "../ui";
import { CashFlowChart, CashFlowLegend, colorsFor, FlowChart, NEUTRAL, RingChart } from "./Charts";
import { download } from "./Expenses";
import { LineCoding } from "./CostCoding";
import { StateReport } from "./StateReport";

/** All money through the bank and cards (FR9), or the P&L on either basis. */
type Basis = "money" | "cash" | "accrual";

interface ReportResponse {
  from: string;
  to: string;
  basis: Basis;
  lines: ReportLine[];
  totals: Record<string, BooksTotals | undefined>;
  categories: SpendCategory[];
  forecast: { date: string; direction: "in" | "out"; amount: number; pipeline: boolean }[];
  forecastFrom: string | null;
  connected: boolean;
  environment: "production" | "sandbox";
  lastSync: { at: string; ok: boolean } | null;
  synced: string[];
  /** Months in the range not yet read from QuickBooks on this basis. */
  unread: string[];
  canExport: boolean;
  projects: CodingProject[];
}

type Preset = "this-month" | "last-month" | "this-quarter" | "last-quarter" | "this-year" | "last-year" | "last-12" | "custom";
const PRESETS: { key: Preset; label: string }[] = [
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
  { key: "this-quarter", label: "This quarter" },
  { key: "last-quarter", label: "Last quarter" },
  { key: "this-year", label: "This year" },
  { key: "last-year", label: "Last year" },
  { key: "last-12", label: "Last 12 months" },
  { key: "custom", label: "Custom" },
];

function rangeFor(p: Preset, thisMonth: string): { from: string; to: string } {
  const y = thisMonth.slice(0, 4);
  const q0 = addMonths(`${y}-01`, Math.floor((Number(thisMonth.slice(5, 7)) - 1) / 3) * 3);
  switch (p) {
    case "this-month":
      return { from: thisMonth, to: thisMonth };
    case "last-month":
      return { from: addMonths(thisMonth, -1), to: addMonths(thisMonth, -1) };
    case "this-quarter":
      return { from: q0, to: addMonths(q0, 2) };
    case "last-quarter":
      return { from: addMonths(q0, -3), to: addMonths(q0, -1) };
    case "last-year":
      return { from: `${Number(y) - 1}-01`, to: `${Number(y) - 1}-12` };
    case "last-12":
      return { from: addMonths(thisMonth, -11), to: thisMonth };
    default:
      return { from: `${y}-01`, to: `${y}-12` };
  }
}

const TABS = [
  { key: "cashflow", label: "Cash flow" },
  { key: "spending", label: "Spending" },
  { key: "income", label: "Income" },
  { key: "flow", label: "Flow" },
  { key: "states", label: "Cost by state" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

const periodLabel = (k: string, g: ReportGrouping) => (g === "month" ? k : k.replace("-", " "));

export function Reports({ tab: asked }: { tab?: string }) {
  const thisMonth = monthOf(today());
  const me = useMe();
  // Cost by state is pay data (TX2): it needs comp.read and period.close as well as books.read.
  const tabs = TABS.filter((t) => t.key !== "states" || (me.can("comp.read") && me.can("period.close")));
  const tab: TabKey = (tabs.find((t) => t.key === asked)?.key ?? "cashflow") as TabKey;
  const setTab = (t: TabKey) => go("reports", t);
  const [preset, setPreset] = useState<Preset>("this-year");
  const [custom, setCustom] = useState(rangeFor("this-year", thisMonth));
  const [grouping, setGrouping] = useState<ReportGrouping>("month");
  const [basis, setBasis] = useState<Basis>("money");
  const [compare, setCompare] = useState<"none" | "previous" | "year">("none");
  const [client, setClient] = useState("");
  const [pipeline, setPipeline] = useState(false);
  const range = preset === "custom" ? custom : rangeFor(preset, thisMonth);
  const q = (r: { from: string; to: string }) => `/books/report?from=${r.from}&to=${r.to}&basis=${basis}`;
  const data = useApi<ReportResponse>(tab === "states" ? null : q(range));
  const cmpRange = compare === "none" ? null : compareRange(range.from, range.to, compare);
  const cmp = useApi<ReportResponse>(cmpRange && tab !== "states" ? q(cmpRange) : null);
  const [exportError, setExportError] = useState<string>();
  const d = data.data;

  // A client filter narrows income; spending isn't tied to a client in QuickBooks, so it stays whole.
  const filter = (lines: ReportLine[]): ReportLine[] => (client ? lines.filter((l) => l.kind === "expense" || (l.clientName ?? NOT_ASSIGNED) === client) : lines);
  const lines = useMemo(() => (d ? filter(d.lines) : []), [d, client]);
  const cmpLines = useMemo(() => (cmp.data ? filter(cmp.data.lines) : []), [cmp.data, client]);
  const clients = useMemo(() => (d ? incomeByClient(d.lines).map((s) => s.key) : []), [d]);

  const exportCsv = async () => {
    setExportError(undefined);
    try {
      download(await api<{ name: string; contentType: string; data: string }>(`/books/report/export?from=${range.from}&to=${range.to}&basis=${basis}`));
    } catch (err) {
      setExportError(errorText(err));
    }
  };

  return (
    <Page title="Reports">
      <TabList selectedValue={tab} onTabSelect={(_, v) => setTab(v.value as TabKey)}>
        {tabs.map((t) => (
          <Tab key={t.key} value={t.key}>
            {t.label}
          </Tab>
        ))}
      </TabList>
      {tab === "states" ? (
        <StateReport />
      ) : (
      <>
      <div className="toolbar">
        <Select size="small" aria-label="Range" value={preset} onChange={(_, v) => setPreset(v.value as Preset)}>
          {PRESETS.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </Select>
        {preset === "custom" && (
          <>
            <DateInput size="small" type="month" value={custom.from} aria-label="From" onChange={(_, v) => v.value && setCustom({ ...custom, from: v.value })} />
            <DateInput size="small" type="month" value={custom.to} aria-label="To" onChange={(_, v) => v.value && setCustom({ ...custom, to: v.value })} />
          </>
        )}
        <Select size="small" aria-label="Group by" value={grouping} onChange={(_, v) => setGrouping(v.value as ReportGrouping)}>
          <option value="month">Monthly</option>
          <option value="quarter">Quarterly</option>
          <option value="year">Yearly</option>
        </Select>
        <Select size="small" aria-label="Basis" value={basis} onChange={(_, v) => setBasis(v.value as Basis)}>
          <option value="money">All money: bank and cards</option>
          <option value="cash">P&L, cash: received and paid</option>
          <option value="accrual">P&L, accrual: invoiced and billed</option>
        </Select>
        <Select size="small" aria-label="Compare" value={compare} onChange={(_, v) => setCompare(v.value as typeof compare)}>
          <option value="none">No comparison</option>
          <option value="previous">Compare with the period before</option>
          <option value="year">Compare with a year earlier</option>
        </Select>
        <Select size="small" aria-label="Client" value={client} onChange={(_, v) => setClient(v.value)}>
          <option value="">Every client</option>
          {clients.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        {d?.canExport && (
          <Button size="small" onClick={() => void exportCsv()}>
            Export CSV
          </Button>
        )}
      </div>
      <ErrorBar message={data.error ?? exportError} />
      {!d ? (
        <Loading />
      ) : !d.synced.length ? (
        <MessageBar intent="info">
          <MessageBarBody>
            Nothing has been read from QuickBooks yet. Connect it and read its history on the <a href={href("books")}>QuickBooks</a> screen.
          </MessageBarBody>
        </MessageBar>
      ) : (
        <>
          {d.environment === "sandbox" && (
            <MessageBar intent="warning">
              <MessageBarBody>These figures come from Intuit's sandbox test company, not 3Seven's books.</MessageBarBody>
            </MessageBar>
          )}
          {d.unread.length > 0 && (
            <MessageBar intent="warning">
              <MessageBarBody>
                {d.unread.length === 1 ? d.unread[0] : `${d.unread.length} months (${d.unread[0]} to ${d.unread.at(-1)})`} {d.unread.length === 1 ? "hasn't" : "haven't"} been read from QuickBooks
                {d.basis === "money" ? " for all money" : ""} yet, so {d.unread.length === 1 ? "it shows" : "they show"} nothing. Click Read history on the <a href={href("books")}>QuickBooks</a> screen.
              </MessageBarBody>
            </MessageBar>
          )}
          <TieOut lines={d.lines} totals={d.totals} basis={d.basis} />
          {tab === "cashflow" ? (
            <CashFlowTab d={d} lines={lines} cmpLines={cmp.data ? cmpLines : null} from={range.from} to={range.to} grouping={grouping} pipeline={pipeline} setPipeline={setPipeline} />
          ) : tab === "spending" ? (
            <SpendingTab lines={lines} cmpLines={cmp.data ? cmpLines : null} categories={d.categories} projects={d.projects} reload={data.reload} />
          ) : tab === "income" ? (
            <IncomeTab lines={lines} cmpLines={cmp.data ? cmpLines : null} from={range.from} to={range.to} grouping={grouping} />
          ) : (
            <FlowTab lines={lines} categories={d.categories} />
          )}
        </>
      )}
      </>
      )}
    </Page>
  );
}

/** FR8: Base Camp's totals against QuickBooks's own, or why they can't be compared yet. */
function TieOut({ lines, totals, basis }: { lines: ReportLine[]; totals: Record<string, BooksTotals | undefined>; basis: Basis }) {
  if (basis === "money") return <Caption1 className="block muted">All money: QuickBooks's bank, card and Undeposited Funds ledgers. Check a month against the bank statement to be sure.</Caption1>;
  const t = tieOut(lines, totals);
  if (!t.months) return null;
  if (t.missing.length) {
    return <Caption1 className="block muted">{`${t.missing.length} of these months were read before QuickBooks's own totals were kept: sync them again on the QuickBooks screen to check they tie.`}</Caption1>;
  }
  const off = Math.abs(t.income - t.booksIncome) + Math.abs(t.expense - t.booksExpense);
  return off < 0.01 ? (
    <Caption1 className="block muted">{`Ties to QuickBooks's own P&L: income ${money(t.booksIncome)}, spending ${money(t.booksExpense)}.`}</Caption1>
  ) : (
    <MessageBar intent="warning">
      <MessageBarBody>{`Base Camp's totals differ from QuickBooks's own P&L by ${money(off)} (income ${money(t.income)} against ${money(t.booksIncome)}, spending ${money(t.expense)} against ${money(t.booksExpense)}). Sync these months again; if it stays, tell Chris.`}</MessageBarBody>
    </MessageBar>
  );
}

const sumKind = (lines: readonly ReportLine[], kind: "income" | "expense") => lines.filter((l) => l.kind === kind && !l.transfer).reduce((t, l) => t + l.amount, 0);

function Change({ now, before }: { now: number; before: number | null }) {
  if (before === null) return null;
  const diff = now - before;
  return (
    <Caption1 className="block muted">
      {diff >= 0 ? "+" : "−"}
      {money(Math.abs(diff))}
      {before ? ` (${diff >= 0 ? "+" : "−"}${pct(Math.abs(diff / before))})` : ""} vs before
    </Caption1>
  );
}

// --- Cash flow (FR2) ---------------------------------------------------------------------------------------

function CashFlowTab(props: { d: ReportResponse; lines: ReportLine[]; cmpLines: ReportLine[] | null; from: string; to: string; grouping: ReportGrouping; pipeline: boolean; setPipeline: (v: boolean) => void }) {
  const [selected, setSelected] = useState<string | null>(null);
  const forecast = props.d.forecast.filter((f) => props.pipeline || !f.pipeline);
  const periods = cashFlowPeriods(props.lines, forecast, props.from, props.to, props.grouping);
  const columns = periods.map((p) => ({ ...p, label: periodLabel(p.key, props.grouping) }));
  const inSum = sumKind(props.lines, "income");
  const outSum = sumKind(props.lines, "expense");
  // Moves between 3Seven's own accounts: each shows as out of one and into another, so count one side.
  const moved = props.lines.filter((l) => l.transfer && l.kind === "expense").reduce((t, l) => t + l.amount, 0);
  const fIn = forecast.filter((f) => f.direction === "in").reduce((t, f) => t + f.amount, 0);
  const fOut = forecast.filter((f) => f.direction === "out").reduce((t, f) => t + f.amount, 0);
  const cmpIn = props.cmpLines ? sumKind(props.cmpLines, "income") : null;
  const cmpOut = props.cmpLines ? sumKind(props.cmpLines, "expense") : null;
  const inPeriod = selected ? props.lines.filter((l) => periodOfLine(l, props.grouping) === selected) : [];
  return (
    <>
      <div className="kpis" aria-label="Cash flow">
        <Kpi label="Money in" value={money(inSum)} sub={<Change now={inSum} before={cmpIn} />} />
        <Kpi label="Money out" value={money(outSum)} sub={<Change now={outSum} before={cmpOut} />} />
        <Kpi label="Net" value={money(inSum - outSum)} tone={inSum - outSum >= 0 ? "good" : "bad"} flag={inSum - outSum >= 0 ? "Saved" : "From reserves"} sub={<Change now={inSum - outSum} before={cmpIn !== null && cmpOut !== null ? cmpIn - cmpOut : null} />} />
        {forecast.length > 0 && <Kpi label="Forecast, rest of range" value={money(fIn - fOut)} sub={`In ${money(fIn)}, out ${money(fOut)}, from the Cash forecast`} />}
        {props.d.basis === "money" && moved > 0 && <Kpi label="Moved between accounts" value={money(moved)} sub="Transfers and card payments: not in or out" />}
      </div>
      <Card className="panel wide">
        <div className="panel-head">
          <Subtitle2>Money in and out</Subtitle2>
          <CashFlowLegend forecast={forecast.length > 0} />
        </div>
        <Caption1>
          {props.d.basis === "money"
            ? "Past periods are every dollar through the bank, card and Undeposited Funds accounts: income, spending, owners' draws and contributions, loans and tax deposits. Moves between those accounts, card payments included, are left out, since they net to nothing."
            : props.d.basis === "cash"
              ? "Past periods are QuickBooks's P&L as received and paid: transfers, loan principal and owners' draws aren't in it."
              : "Past periods are QuickBooks's P&L as invoiced and billed: transfers, loan principal and owners' draws aren't in it."}{" "}
          {props.d.forecastFrom ? `From ${day(props.d.forecastFrom)} the columns are the Cash forecast, paler, with the net dashed.` : props.d.basis === "accrual" ? "The forecast shows on the cash and all-money bases only." : ""} Click a period for its detail.
        </Caption1>
        {props.d.forecast.length > 0 && <Switch label="Include the pipeline in the forecast, weighted" checked={props.pipeline} onChange={(_, v) => props.setPipeline(v.checked)} />}
        <CashFlowChart columns={columns} selected={selected} onSelect={setSelected} />
        <div className="table-scroll">
          <table className="edit-table cash-table">
            <thead>
              <tr>
                <th />
                {columns.map((c) => (
                  <th key={c.key} className={`num${c.key === selected ? " selected" : ""}`}>
                    {c.label}
                    {c.forecast ? "*" : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(["in", "out", "net"] as const).map((k) => (
                <tr key={k} className={k === "net" ? "total" : undefined}>
                  <th scope="row">{k === "in" ? "Money in" : k === "out" ? "Money out" : "Net"}</th>
                  {columns.map((c) => (
                    <td key={c.key} className={`num${c.key === selected ? " selected" : ""}`}>
                      {Math.abs(c[k]) < 0.5 ? "—" : money(c[k])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {columns.some((c) => c.forecast) && <Caption1 className="muted">* Includes the forecast.</Caption1>}
      </Card>
      {selected && (
        <Card className="panel wide">
          <div className="panel-head">
            <Subtitle2>{periodLabel(selected, props.grouping)}</Subtitle2>
            <Button size="small" appearance="subtle" onClick={() => setSelected(null)}>
              Close
            </Button>
          </div>
          <div className="two-col">
            <SliceTable title="In, by client" slices={incomeByClient(inPeriod)} />
            <SliceTable title="Out, by category" slices={spendingAt(inPeriod, props.d.categories)} />
          </div>
          {columns.find((c) => c.key === selected)?.forecast && <Caption1 className="muted">The forecast part of this period is on the Cash screen.</Caption1>}
        </Card>
      )}
    </>
  );
}

const periodOfLine = (l: ReportLine, g: ReportGrouping) => {
  const m = monthOf(l.date);
  return g === "month" ? m : g === "year" ? m.slice(0, 4) : `${m.slice(0, 4)}-Q${Math.floor((Number(m.slice(5, 7)) - 1) / 3) + 1}`;
};

function SliceTable({ title, slices }: { title: string; slices: Slice[] }) {
  const total = slices.reduce((t, s) => t + s.amount, 0);
  return (
    <div>
      <Body1>
        <strong>{title}</strong>
      </Body1>
      {slices.length === 0 ? (
        <Body1 className="muted">None.</Body1>
      ) : (
        <table className="edit-table">
          <tbody>
            {slices.map((s) => (
              <tr key={s.key}>
                <td>{s.label}</td>
                <td className="num">{money(s.amount)}</td>
                <td className="num muted">{total ? pct(s.amount / total) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- Spending and income (FR4, FR5) --------------------------------------------------------------------------

const isLeftover = (label: string) => label === "Unmapped" || label === NOT_ASSIGNED || label.startsWith("Other");

/** The ring, a legend table with shares and comparison, and the lines behind a slice at the last level. */
function RingPanel(props: {
  title: string;
  caption: string;
  slices: Slice[];
  cmp: Slice[] | null;
  colors: Map<string, string>;
  onOpen: (s: Slice) => void;
  canOpen: (s: Slice) => boolean;
  crumbs?: { label: string; onClick: () => void }[];
}) {
  const [all, setAll] = useState(false);
  const total = props.slices.reduce((t, s) => t + s.amount, 0);
  const cmpBy = new Map((props.cmp ?? []).map((s) => [s.key, s.amount]));
  const shown = all ? props.slices : props.slices.slice(0, 11);
  const rest = props.slices.slice(11).reduce((t, s) => t + s.amount, 0);
  const ring = [...shown.map((s) => ({ ...s, color: props.colors.get(s.key) ?? NEUTRAL })), ...(rest > 0 && !all ? [{ key: "__rest", label: "Everything else", amount: rest, color: NEUTRAL }] : [])];
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>{props.title}</Subtitle2>
        {props.crumbs && props.crumbs.length > 0 && (
          <span className="crumbs">
            {props.crumbs.map((c, i) => (
              <span key={c.label}>
                {i > 0 && " › "}
                <button type="button" className="link-button" onClick={c.onClick}>
                  {c.label}
                </button>
              </span>
            ))}
          </span>
        )}
      </div>
      {props.slices.length === 0 ? (
        <Body1 className="muted">Nothing in this range.</Body1>
      ) : (
        <div className="ring-layout">
          <RingChart slices={ring} total={total} caption={props.title} onSelect={(k) => (k === "__rest" ? setAll(true) : props.slices.find((s) => s.key === k && props.canOpen(s)) && props.onOpen(props.slices.find((s) => s.key === k)!))} />
          <div className="table-scroll">
            <table className="edit-table">
              <thead>
                <tr>
                  <th>{props.caption}</th>
                  <th className="num">Amount</th>
                  <th className="num">Share</th>
                  {props.cmp && <th className="num">Before</th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => (
                  <tr key={s.key}>
                    <td>
                      <span className="swatch" style={{ background: props.colors.get(s.key) ?? NEUTRAL }} aria-hidden="true" />
                      {props.canOpen(s) ? (
                        <button type="button" className="link-button" onClick={() => props.onOpen(s)}>
                          {s.label}
                        </button>
                      ) : (
                        s.label
                      )}
                    </td>
                    <td className="num">{money(s.amount)}</td>
                    <td className="num muted">{total ? pct(s.amount / total) : ""}</td>
                    {props.cmp && <td className="num muted">{cmpBy.has(s.key) ? money(cmpBy.get(s.key)!) : "—"}</td>}
                  </tr>
                ))}
                <tr className="total">
                  <td>Total</td>
                  <td className="num">{money(total)}</td>
                  <td />
                  {props.cmp && <td className="num muted">{money((props.cmp ?? []).reduce((t, s) => t + s.amount, 0))}</td>}
                </tr>
              </tbody>
            </table>
            {props.slices.length > 11 && (
              <Button size="small" appearance="subtle" onClick={() => setAll(!all)}>
                {all ? "Show fewer" : `Show all ${props.slices.length}`}
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function LinesTable({ title, lines, onClose, projects, reload }: { title: string; lines: ReportLine[]; onClose: () => void; projects?: CodingProject[]; reload?: () => void }) {
  const coding = !!projects && lines.some((l) => l.kind === "expense" && l.codingKey);
  return (
    <Card className="panel wide">
      <div className="panel-head">
        <Subtitle2>{title}</Subtitle2>
        <Button size="small" appearance="subtle" onClick={onClose}>
          Close
        </Button>
      </div>
      <div className="table-scroll">
        <table className="edit-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Transaction</th>
              <th>Payee or payer</th>
              <th>Account</th>
              <th>Memo</th>
              <th className="num">Amount</th>
              {coding && <th>Project and who bears it</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <td>{day(l.date)}</td>
                <td>
                  {l.txnType} {l.docNumber ?? ""}
                  {l.assigned && (
                    <Badge size="small" appearance="tint" className="tab-count">
                      Assigned in Base Camp
                    </Badge>
                  )}
                </td>
                <td>{l.partyName ?? ""}</td>
                <td>{l.accountName}</td>
                <td>{l.memo ?? ""}</td>
                <td className="num">
                  {money(l.amount)}
                  {l.allocation?.length ? <Caption1 className="block muted">Payroll total, shared out by Base Camp pay</Caption1> : null}
                </td>
                {coding && (
                  <td>
                    {l.codingKey && l.treatment && l.codingSource ? (
                      <LineCoding codingKey={l.codingKey} coding={{ code: l.project, treatment: l.treatment, source: l.codingSource }} projects={projects!} onSaved={() => reload?.()} />
                    ) : null}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SpendingTab(props: { lines: ReportLine[]; cmpLines: ReportLine[] | null; categories: SpendCategory[]; projects: CodingProject[]; reload: () => void }) {
  const { categories, projects } = props;
  const [path, setPath] = useState<{ key: string; label: string }[]>([]);
  const [openLines, setOpenLines] = useState<Slice | null>(null);
  const [view, setView] = useState<"category" | "project">("category");
  const [billed, setBilled] = useState(true);
  // Billed-back costs pass through to clients at cost (FR10.4): shown or left out, with what was rebilled beside them.
  const billedBack = props.lines.filter((l) => l.kind === "expense" && !l.transfer && l.treatment === "billed").reduce((t, l) => t + l.amount, 0);
  const rebilled = props.lines.filter((l) => l.kind === "income" && l.rebill).reduce((t, l) => t + l.amount, 0);
  const keep = (ls: ReportLine[]) => (billed ? ls : ls.filter((l) => l.treatment !== "billed"));
  const lines = keep(props.lines);
  const cmpLines = props.cmpLines ? keep(props.cmpLines) : null;
  const toolbar = (
    <div className="toolbar">
      <Select size="small" aria-label="View" value={view} onChange={(_, v) => (setView(v.value as typeof view), setOpenLines(null))}>
        <option value="category">By category</option>
        <option value="project">By project</option>
      </Select>
      <Switch label="Include billed-back costs" checked={billed} onChange={(_, v) => setBilled(v.checked)} />
      {billedBack !== 0 && (
        <Caption1 className="muted">
          Billed back {money(billedBack)}; rebilled to clients {money(rebilled)}
        </Caption1>
      )}
    </div>
  );
  const within = path.at(-1)?.key;
  const slices = spendingAt(lines, categories, within);
  const cmp = cmpLines ? spendingAt(cmpLines, categories, within) : null;
  // Top categories keep their hue at every range (the category list's order); deeper levels by size.
  const topOrder = categories.filter((c) => !c.parentId && c.id !== UNMAPPED).map((c) => c.id);
  const colors = within ? colorsFor(slices.map((s) => s.key), (k) => isLeftover(slices.find((s) => s.key === k)?.label ?? "")) : colorsFor([...topOrder, UNMAPPED], (k) => k === UNMAPPED);
  const isCategory = (k: string) => categories.some((c) => c.id === k);
  const node = within ? categories.find((c) => c.id === within) : undefined;
  if (view === "project")
    return (
      <>
        {toolbar}
        <ProjectSpending lines={lines} projects={projects} reload={props.reload} />
      </>
    );
  return (
    <>
      {toolbar}
      <RingPanel
        title={node ? node.name : "Spending by category"}
        caption={node?.people ? "Person" : within ? "Category or account" : "Category"}
        slices={slices}
        cmp={cmp}
        colors={colors}
        crumbs={path.length ? [{ label: "All spending", onClick: () => (setPath([]), setOpenLines(null)) }, ...path.map((p, i) => ({ label: p.label, onClick: () => (setPath(path.slice(0, i + 1)), setOpenLines(null)) }))] : undefined}
        canOpen={() => true}
        onOpen={(s) => {
          setOpenLines(null);
          if (isCategory(s.key)) setPath([...path, { key: s.key, label: s.label }]);
          else setOpenLines(s);
        }}
      />
      {node?.people === "employees" && <Caption1 className="muted">QuickBooks holds payroll as one total from JourneyHR, so each person's share, partners' guaranteed payments included, is allocated from their Base Camp pay that month.</Caption1>}
      {openLines && <LinesTable title={openLines.label} lines={linesFor(lines, "expense", openLines.key, within)} onClose={() => setOpenLines(null)} projects={projects} reload={props.reload} />}
    </>
  );
}

/** Spending by project and who bears it (FR10.5); a row opens to its lines, which can be coded there. */
function ProjectSpending({ lines, projects, reload }: { lines: ReportLine[]; projects: CodingProject[]; reload: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const rows = spendingByProject(lines);
  const name = (code: string) => {
    if (!code) return "No project";
    const p = projects.find((x) => x.code === code);
    return p ? `${code} · ${p.name}${p.internal ? " (internal)" : ` (${p.clientName})`}` : code;
  };
  const sum = (k: "project" | "billed" | "company" | "total") => rows.reduce((t, r) => t + r[k], 0);
  return (
    <>
      <Card className="panel wide">
        <Subtitle2>Spending by project</Subtitle2>
        <Caption1 className="muted">Code costs to projects on the QuickBooks screen's Code costs tab, an account's default on its Accounts tab, or a line here. Internal codes are shared costs.</Caption1>
        <div className="table-scroll">
          <table className="edit-table">
            <thead>
              <tr>
                <th>Project</th>
                <th className="num">Project cost</th>
                <th className="num">Billed back</th>
                <th className="num">Company cost</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.code || "none"}>
                  <td>
                    <button type="button" className="link-button" onClick={() => setOpen(r.code)}>
                      {name(r.code)}
                    </button>
                  </td>
                  <td className="num">{r.project ? money(r.project) : "—"}</td>
                  <td className="num">{r.billed ? money(r.billed) : "—"}</td>
                  <td className="num">{r.company ? money(r.company) : "—"}</td>
                  <td className="num">{money(r.total)}</td>
                </tr>
              ))}
              <tr className="total">
                <td>Total</td>
                <td className="num">{money(sum("project"))}</td>
                <td className="num">{money(sum("billed"))}</td>
                <td className="num">{money(sum("company"))}</td>
                <td className="num">{money(sum("total"))}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
      {open !== null && (
        <LinesTable
          title={name(open)}
          lines={lines.filter((l) => l.kind === "expense" && !l.transfer && (l.project ?? "") === open)}
          onClose={() => setOpen(null)}
          projects={projects}
          reload={reload}
        />
      )}
    </>
  );
}

function IncomeTab({ lines, cmpLines, from, to, grouping }: { lines: ReportLine[]; cmpLines: ReportLine[] | null; from: string; to: string; grouping: ReportGrouping }) {
  const [open, setOpen] = useState<Slice | null>(null);
  const slices = incomeByClient(lines);
  const colors = colorsFor(slices.map((s) => s.key).sort((a, b) => a.localeCompare(b)), (k) => k === NOT_ASSIGNED);
  const periods = cashFlowPeriods(lines.filter((l) => l.kind === "income"), [], from, to, grouping).map((p) => p.key);
  const byClientPeriod = new Map<string, number>();
  for (const l of lines) if (l.kind === "income") byClientPeriod.set(`${l.clientName ?? NOT_ASSIGNED}|${periodOfLine(l, grouping)}`, (byClientPeriod.get(`${l.clientName ?? NOT_ASSIGNED}|${periodOfLine(l, grouping)}`) ?? 0) + l.amount);
  return (
    <>
      <RingPanel title="Income by client" caption="Client" slices={slices} cmp={cmpLines ? incomeByClient(cmpLines) : null} colors={colors} canOpen={() => true} onOpen={setOpen} />
      {open && <LinesTable title={open.label} lines={linesFor(lines, "income", open.key)} onClose={() => setOpen(null)} />}
      {periods.length > 1 && slices.length > 0 && (
        <Card className="panel wide">
          <Subtitle2>By {grouping}, client against client</Subtitle2>
          <div className="table-scroll">
            <table className="edit-table cash-table">
              <thead>
                <tr>
                  <th />
                  {periods.map((p) => (
                    <th key={p} className="num">
                      {periodLabel(p, grouping)}
                    </th>
                  ))}
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {slices.map((s) => (
                  <tr key={s.key}>
                    <th scope="row">
                      <span className="swatch" style={{ background: colors.get(s.key) ?? NEUTRAL }} aria-hidden="true" />
                      {s.label}
                    </th>
                    {periods.map((p) => {
                      const v = byClientPeriod.get(`${s.key}|${p}`) ?? 0;
                      return (
                        <td key={p} className="num">
                          {Math.abs(v) < 0.5 ? "—" : money(v)}
                        </td>
                      );
                    })}
                    <td className="num">{money(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

// --- Flow (FR3) ---------------------------------------------------------------------------------------------

function FlowTab({ lines, categories }: { lines: ReportLine[]; categories: SpendCategory[] }) {
  const links = flowLinks(lines, categories);
  const clientIds = [...new Set(links.filter((l) => l.to === "Income").map((l) => l.from))];
  const catIds = [...new Set(links.filter((l) => l.from === "Income").map((l) => l.to))];
  const cc = colorsFor(clientIds, (k) => k === "Other clients" || k === "From reserves" || k === NOT_ASSIGNED);
  const topOrder = categories.filter((c) => !c.parentId && c.id !== UNMAPPED).map((c) => c.name);
  const sc = colorsFor([...topOrder, "Unmapped", "Saved"], (k) => k === "Unmapped" || k === "Saved");
  const nodes = [...clientIds.map((id) => ({ id, label: id, color: cc.get(id) ?? NEUTRAL })), ...catIds.map((id) => ({ id, label: id, color: sc.get(id) ?? NEUTRAL }))];
  return (
    <Card className="panel wide">
      <Subtitle2>Where the money came from and went</Subtitle2>
      <Caption1>Income by client on the left, spending by category on the right. What income didn't spend shows as Saved; spending beyond it, as From reserves.</Caption1>
      {links.length === 0 ? <Body1 className="muted">Nothing in this range.</Body1> : <FlowChart links={links} nodes={nodes} />}
    </Card>
  );
}
